/*
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2022 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getDealerState } from "@insite/client-framework/Store/Data/Dealers/DealersSelectors";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import Button from "@insite/mobius/Button";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import MarketoFormWrapper from "@stoneage/App/Components/MarketoFormModal/MarketoFormWrapper";
import { CustomAppState } from "@stoneage/App/Store/Reducers";
import parse from "html-react-parser";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const isDealerPage = getCurrentPage(state)?.type === "DealerDetailsPage";
    const location = getLocation(state);
    const parsedQuery = parseQueryString<{ id?: string; invite?: string }>(location.search);
    const id = parsedQuery.id;
    const dealerState = isDealerPage && id ? getDealerState(state, id)?.value : {};
    return {
        dealerState,
        isDealerPage,
        modalForms: (state as CustomAppState).xMarketoModalForms.modalFormsData,
    };
};

const mapDispatchToProps = {};

export interface MarketoFormStyles {
    formWrapper?: InjectableCss;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    modal?: ModalPresentationProps;
}

export const styles: MarketoFormStyles = {
    formWrapper: {
        css: css`
            ${({ width }: any) => {
                if (width) {
                    return css`
                        max-width: ${width}px!important;
                        width: 100%;
                        overflow-x: auto;
                    `;
                }
            }}

            ${({ height }: any) => {
                if (height) {
                    return css`
                        max-height: ${height}px!important;
                        height: 100%;
                        overflow-y: auto;
                    `;
                }
            }}
        `,
    },
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
};

type ModalProps = {
    title?: string;
    formDescription?: string;
    displayAs: "button" | "form";
    height?: number;
    width?: number;
    buttonText?: string;
    marketoSettings: {
        formId: string;
        baseUrl: string;
        munchkinId: string;
    };
    instructionMessage?: string;
    customCSS?: string;
    // We use formType to determine the google analytics event triggering on success
    formType?: "contactUs" | "requestQuote" | "requestRent" | "searchDealer" | "newsSubscription" | "bookConsultation";
};

type Props = ModalProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasShellContext;

const MarketoFormModal = ({
    title,
    formDescription,
    displayAs,
    height,
    width,
    buttonText,
    isDealerPage,
    dealerState,
    shellContext,
    marketoSettings: { formId },
    instructionMessage,
    customCSS,
    modalForms,
    formType,
}: Props) => {
    const parsedTitle = title ? parse(title, parserOptions) : "";
    const [showModal, setShowModal] = useState(false);

    if (displayAs === "form") {
        return (
            <StyledWrapper {...styles.formWrapper} {...{ width, height }}>
                {title && (
                    <Typography
                        css={css`
                            display: block;
                        `}
                    >
                        {parsedTitle}
                    </Typography>
                )}
                {formDescription && (
                    <Typography
                        css={css`
                            display: block;
                        `}
                    >
                        {parse(formDescription, parserOptions)}
                    </Typography>
                )}

                <MarketoFormWrapper
                    formId={formId}
                    formWidth={width}
                    formHeight={height}
                    customCss={customCSS}
                    inModal={false}
                    confirmationMessage={instructionMessage}
                    useRenderFunction={true}
                    formType={formType}
                />
            </StyledWrapper>
        );
    }

    return (
        <>
            <Button
                onClick={() => {
                    setShowModal(true);
                }}
            >
                {buttonText}
            </Button>

            <Modal
                size={width || 700}
                headline={parsedTitle}
                isOpen={showModal}
                handleClose={() => setShowModal(false)}
            >
                {formDescription && (
                    <Typography
                        css={css`
                            display: block;
                        `}
                    >
                        {parse(formDescription, parserOptions)}
                    </Typography>
                )}

                <MarketoFormWrapper
                    formWidth={width}
                    formHeight={height}
                    confirmationMessage={instructionMessage}
                    formId={formId}
                    customCss={customCSS}
                    inModal={false}
                    useRenderFunction={true}
                    formType={formType}
                />
            </Modal>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withIsInShell(MarketoFormModal));
